// Tiny observable store. One state object, shallow-merge updates, subscriber
// callbacks. The three views all read from the same result set so they stay
// in sync (spec 6.4 to 6.6).

import type { EventCategory, EventsResponse, NormalizedEvent } from '../types/event';
import { expandCategories } from './categories';
import { haversineMiles, localDayKey } from './format';

export type ViewName = 'list' | 'calendar' | 'map';
export type Status = 'idle' | 'loading' | 'ready' | 'error';
export type SortBy = 'soonest' | 'nearest';

export interface LocationInfo {
  lat: number;
  lng: number;
  label: string; // e.g. "Denver, CO 80202" or "Current location"
}

export interface AppState {
  status: Status;
  error?: string;
  events: NormalizedEvent[]; // raw result set from the server
  meta?: EventsResponse['meta'];
  location?: LocationInfo;
  keyword: string;
  radiusMiles: number;
  activeView: ViewName;
  categoryFilter: Set<EventCategory>; // empty set means "all"
  sortBy: SortBy; // list ordering
  selectedDay?: string; // local day key (YYYY-MM-DD) for calendar filtering
  selectedEventId?: string;
  focusEventId?: string; // transient: pan the map to this event
}

const initialState: AppState = {
  status: 'idle',
  events: [],
  keyword: '',
  radiusMiles: 25,
  activeView: 'list',
  categoryFilter: new Set(),
  sortBy: 'soonest',
};

type Listener = (state: AppState) => void;

class Store {
  private state: AppState = { ...initialState };
  private listeners = new Set<Listener>();

  get(): AppState {
    return this.state;
  }

  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.state);
  }

  // --- actions ---

  toggleCategory(cat: EventCategory): void {
    const next = new Set(this.state.categoryFilter);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    this.set({ categoryFilter: next });
  }

  clearCategoryFilter(): void {
    this.set({ categoryFilter: new Set() });
  }

  setSort(sortBy: SortBy): void {
    if (this.state.sortBy === sortBy) return;
    this.set({ sortBy });
  }

  setView(view: ViewName): void {
    this.set({ activeView: view });
  }

  selectDay(day?: string): void {
    // Toggle off if the same day is selected again.
    this.set({ selectedDay: this.state.selectedDay === day ? undefined : day });
  }

  focusEvent(id: string): void {
    this.set({ selectedEventId: id, focusEventId: id });
  }
}

export const store = new Store();

// Derived selector: events after client-side category, day, and expiry checks.
// We re-run expiry here because a cached payload can age past midnight.
export function visibleEvents(state: AppState): NormalizedEvent[] {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const cutoff = startOfToday.getTime();

  const cats = state.categoryFilter.size > 0 ? expandCategories(state.categoryFilter) : null;
  const filtered = state.events.filter((e) => {
    const ref = e.end ?? e.start;
    const t = Date.parse(ref);
    if (!Number.isNaN(t) && t < cutoff) return false; // expired today
    if (cats && !cats.has(e.category)) return false;
    if (state.selectedDay && localDayKey(e.start) !== state.selectedDay) return false;
    return true;
  });
  return sortEvents(filtered, state);
}

// Distance in miles from the searched location to an event, or Infinity when
// either side lacks coordinates (so coordless events sort to the bottom).
export function eventDistanceMiles(event: NormalizedEvent, state: AppState): number {
  if (!state.location) return Infinity;
  if (typeof event.lat !== 'number' || typeof event.lng !== 'number') return Infinity;
  return haversineMiles(state.location.lat, state.location.lng, event.lat, event.lng);
}

function sortEvents(events: NormalizedEvent[], state: AppState): NormalizedEvent[] {
  const out = [...events];
  if (state.sortBy === 'nearest' && state.location) {
    out.sort((a, b) => eventDistanceMiles(a, state) - eventDistanceMiles(b, state));
  } else {
    out.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  }
  return out;
}

// Events for a given month (ignores the day filter) for calendar counts.
export function monthEvents(state: AppState): NormalizedEvent[] {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const cutoff = startOfToday.getTime();
  const cats = state.categoryFilter.size > 0 ? expandCategories(state.categoryFilter) : null;
  return state.events.filter((e) => {
    const ref = e.end ?? e.start;
    const t = Date.parse(ref);
    if (!Number.isNaN(t) && t < cutoff) return false;
    if (cats && !cats.has(e.category)) return false;
    return true;
  });
}
